import React, { ReactEventHandler, useState } from 'react';
import { gql, useMutation } from '@nerdwallet/apollo';

import { Box } from '@nerdwallet/currency/components/Box';
import { Button } from '@nerdwallet/currency/components/Button';
import { CheckboxOption } from '@nerdwallet/currency/components/CheckboxOption';
import { Link } from '@nerdwallet/currency/components/Link';
import { SecureEncryptionMessage } from '@nerdwallet/currency/components/SecureEncryptionMessage';
import { Stack } from '@nerdwallet/currency/components/Stack';
import { Title } from '@nerdwallet/currency/components/Title';
import { Text } from '@nerdwallet/currency/components/Text';
import { Type } from '@nerdwallet/currency/components/Type';
import { PlaidStep, useStepper } from './plaid-modal-stepper';
import ErrorBanner from '@components/error-banner/error-banner';
import DialogContentWithCloseButton from '@components/mui/dialog-content-with-close-button';
import { usePlaidModalVisibility } from './plaid-modal-visibility-context';

export const PLAID_TERMS_OF_SERVICE_ACKNOWLEDGEMENT_TYPE = 'PLAID_LINK';
export const PLAID_TERMS_OF_SERVICE_ACKNOWLEDGEMENT_VERSION = '1';
export const PLAID_TERMS_OF_SERVICE_ACKNOWLEDGEMENT_SOURCE = 'MY_NERDWALLET';

const acknowledgeTermsOfServiceMutation = gql`
  mutation AcknowledgeTermsOfService($input: AcknowledgeTermsOfServiceInput!) {
    acknowledgeTermsOfService(input: $input) {
      success
    }
  }
`;

const PlaidTermsConsent = ({ handleCloseModal }) => {
  const [hasAgreed, setHasAgreed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { setRequiresPlaidTermsConsent } = useStepper();

  const [acknowledgeTermsOfService, { loading }] = useMutation(
    acknowledgeTermsOfServiceMutation,
    {
      variables: {
        input: {
          type: PLAID_TERMS_OF_SERVICE_ACKNOWLEDGEMENT_TYPE,
          version: PLAID_TERMS_OF_SERVICE_ACKNOWLEDGEMENT_VERSION,
          source: PLAID_TERMS_OF_SERVICE_ACKNOWLEDGEMENT_SOURCE,
        },
      },
    },
  );

  const handleAgreeChange: ReactEventHandler<HTMLInputElement> = (e) => {
    setHasAgreed((e.target as HTMLInputElement).checked);
  };

  const { setShowPlaidVisibility } = usePlaidModalVisibility();

  const handleContinueClick = async () => {
    try {
      const result = await acknowledgeTermsOfService();
      if (result?.data?.acknowledgeTermsOfService?.success) {
        setRequiresPlaidTermsConsent(false);
        setShowPlaidVisibility({
          show: true,
          plaidStep: PlaidStep.PLAID_LINK,
        });
      }
    } catch (e: any) {
      // The user already agreed to the terms. Perhaps the user clicked "Continue" twice,
      // or otherwise landed on this screen erroneously. Let's just allow the user to proceed.
      if (e?.message?.includes('SQL IntegrityError')) {
        setRequiresPlaidTermsConsent(false);
        setShowPlaidVisibility({
          show: true,
          plaidStep: PlaidStep.PLAID_LINK,
        });
      } else {
        setErrorMessage(
          'Hm. Looks like there was a technical issue. Try again in a bit.',
        );
      }
    }
  };

  return (
    <DialogContentWithCloseButton onClose={handleCloseModal}>
      <Stack spacing={4}>
        <Stack spacing={3}>
          <Title size="small">
            Please agree to these terms before linking with Plaid
          </Title>

          <Text>
            Just crossing our t{`'`}s and dotting our i{`'`}s.
          </Text>
        </Stack>
        <Stack spacing={3}>
          <CheckboxOption
            checked={hasAgreed}
            label="I agree"
            onChange={handleAgreeChange}
          />

          <Type size={1} light>
            Account linking is facilitated through Plaid, a third-party service.
            Support for all financial institutions, including certain credit
            unions and smaller banks, is not guaranteed and lies outside
            NerdWallet’s control. Please verify your institution’s compatibility
            with Plaid prior to use. By signing up for this NerdWallet service,
            you hereby authorize Plaid to access and transmit information
            relating to your linked account(s) to NerdWallet for use in a manner
            consistent with NerdWallet{`'`}s{' '}
            <Link
              href="https://www.nerdwallet.com/blog/terms-of-use/"
              target="_blank"
            >
              Terms of Use
            </Link>{' '}
            and{' '}
            <Link
              href="https://www.nerdwallet.com/blog/privacy-policy/"
              target="_blank"
            >
              Privacy Policy
            </Link>
            . We may use your account information to evaluate your eligibility
            for other products or services and to modify or develop our products
            and services to better serve you. Plaid{`'`}s access and use of data
            may differ. To disconnect your account from Plaid, visit
            my.plaid.com. By using this service, you hereby confirm you have
            read and agree to NerdWallet{`'`}s{' '}
            <Link
              href="https://www.nerdwallet.com/blog/terms-of-use/"
              target="_blank"
            >
              Terms of Use
            </Link>{' '}
            and{' '}
            <Link
              href="https://www.nerdwallet.com/blog/privacy-policy/"
              target="_blank"
            >
              Privacy Policy
            </Link>
            .
          </Type>
        </Stack>
        {errorMessage && <ErrorBanner errorMessage={errorMessage} />}
        <Button
          block
          primary
          loading={loading}
          disabled={!hasAgreed}
          onClick={handleContinueClick}
        >
          Continue
        </Button>
      </Stack>

      <Box marginTop="3-half">
        <SecureEncryptionMessage />
      </Box>
    </DialogContentWithCloseButton>
  );
};

export default PlaidTermsConsent;
